import { AxiosResponse } from 'axios'
import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import { httpClient } from 'shared/api/http-client'
import { ApiPaginationResponseInterface } from 'shared/types/api-pagination-response-interface'
import { addQueryParam } from 'shared/utils/add-query-param'
import { BanMemberRequestBody, MemberInterface } from 'modules/community/types/member-interface'
import {
  ApiSearchPaginationResponseInterface,
  PostInterface,
  SearchPostInterface,
} from 'modules/community/types/post-interface'

const PAGINATION_ORDER_KEY = 'pagination[order]'
const PAGINATION_START_FROM_KEY = 'pagination[startFrom]'
const PAGINATION_LIMIT_KEY = 'pagination[limit]'

export const getPostsByLastId = (
  lastPostId: number,
  communityPath?: string,
): Promise<AxiosResponse<ApiPaginationResponseInterface<PostInterface>, unknown, {}>> => {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to get posts'))
  }
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/posts/list?${searchQuery.toString()}`,
  )
}
export const getScheduledPostsByCursor = (
  lastPostId: number,
  communityPath?: string,
): Promise<AxiosResponse<ApiPaginationResponseInterface<PostInterface>, unknown, {}>> => {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to get posts'))
  }
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'prev')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/scheduled/list?${searchQuery.toString()}`,
  )
}

export const getTopicPostsByLastId = (
  lastPostId: number,
  communityPath?: string,
  topicPath?: string,
): Promise<AxiosResponse<ApiPaginationResponseInterface<PostInterface>, any, {}>> => {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to get topic posts'))
  }
  if (!topicPath) {
    return Promise.reject(new Error('topicPath is required to get topic posts'))
  }
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/topic/${topicPath}/posts/list?${searchQuery.toString()}`,
  )
}

export const declinePost = (
  postId: number[],
  communityPath?: string,
): Promise<AxiosResponse<any, any, {}>> => {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to decline posts'))
  }
  return httpClient.post(`${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/decline`, {
    ids: postId,
  })
}

export function getAllPendingPostsByLastId(
  filter: string,
  controller: AbortController,
  communityPath?: string,
  lastPostId?: number,
): Promise<AxiosResponse<ApiPaginationResponseInterface<PostInterface>, any, {}>> {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to get all pending posts'))
  }
  if (lastPostId === undefined) {
    return Promise.reject(new Error('lastPostId is required to get all pending posts'))
  }
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)
  addQueryParam(searchQuery, 'filter[searchBy]', filter)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/list?${searchQuery.toString()}`,
    {
      signal: controller?.signal,
    },
  )
}

export async function getOwnPendingPostsByLastId(
  lastPostId: number,
  communityPath?: string,
): Promise<AxiosResponse<ApiPaginationResponseInterface<PostInterface>, any, {}>> {
  if (!communityPath) {
    throw new Error('communityPath is required to get own pending posts')
  }
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, PAGINATION_LIMIT_KEY, '10')
  addQueryParam(searchQuery, PAGINATION_ORDER_KEY, 'desc')
  addQueryParam(searchQuery, PAGINATION_START_FROM_KEY, lastPostId === 0 ? undefined : lastPostId)

  return httpClient.get<ApiPaginationResponseInterface<PostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/my-pending-posts/list?${searchQuery.toString()}`,
  )
}

export const deletePost = (postId: number): Promise<AxiosResponse<void>> => {
  return httpClient.delete<void>(`/api/community/post/${postId}`)
}

export function banUser(
  userId: number,
  communityPath?: string,
  body?: BanMemberRequestBody,
): Promise<AxiosResponse<unknown, unknown, {}>> {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to ban user'))
  }

  return httpClient.post(
    `${COMMUNITY_API_PREFIX}/${communityPath}/banned-users/${userId}`,
    body || {},
  )
}

export const unbanUser = (
  userId: number,
  communityPath?: string,
): Promise<AxiosResponse<unknown, unknown, {}>> => {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to unban user'))
  }

  return httpClient.delete(`${COMMUNITY_API_PREFIX}/${communityPath}/banned-users/${userId}`)
}

export const deleteUser = (
  userId: number,
  communityPath?: string,
): Promise<AxiosResponse<unknown, unknown, {}>> => {
  if (!communityPath) {
    return Promise.reject(new Error('communityPath is required to delete user'))
  }
  return httpClient.delete<MemberInterface>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/member/${userId}`,
  )
}

export async function searchPosts({
  communityPath,
  q,
  scrollId,
  limit,
  topicPath,
}: {
  communityPath?: string
  q: string | undefined
  scrollId: string | null
  limit: number
  topicPath?: string
}): Promise<AxiosResponse<ApiSearchPaginationResponseInterface<SearchPostInterface>, unknown, {}>> {
  if (!communityPath) {
    throw new Error('communityPath is required to search posts')
  }
  const searchQuery = new URLSearchParams()

  addQueryParam(searchQuery, 'pagination[startFrom]', scrollId)
  addQueryParam(searchQuery, 'searchBy', q)
  addQueryParam(searchQuery, 'pagination[limit]', limit)
  addQueryParam(searchQuery, 'topicPath', topicPath)

  return httpClient.get<ApiSearchPaginationResponseInterface<SearchPostInterface>>(
    `${COMMUNITY_API_PREFIX}/${communityPath}/search?${searchQuery.toString()}`,
  )
}
